import {
  teamsHighContrastTheme,
  Theme,
  themeToTokensObject,
  createLightTheme,
  createTeamsDarkTheme,
  tokens,
} from '@fluentui/react-components';

export const defaultThemeName = 'light';
type CustomTokens = Record<string, string | number>;
type CustomTheme = Theme & CustomTokens;
type Modes = {
  light: CustomTokens
  dark: CustomTokens
  contrast: CustomTokens
};

const commonSpacings: CustomTokens = {
  spacingVertical4XL: '40px',
  spacingVertical5XL: '48px',
  spacingVertical10XL: '88px',
  spacingHorizontal4XL: '40px',
  spacingHorizontal8XL: '72px',
};

const commonFontSizes: CustomTokens = {
  fontSizeBase50: '8px',
  fontSizeBase450: '18px',
};

const commonLinetHeight: CustomTokens = {
  lineHeightBase450: '24px',
};

const commonTokens: CustomTokens = {
  colorNeutralBackground7: tokens.colorNeutralBackground1,
  colorNeutralForeground5: tokens.colorNeutralForeground1,
  colorNeutralCardBackground2: tokens.colorNeutralCardBackground,
  colorBrandBackgroundInvertedHover1: tokens.colorBrandBackgroundInvertedSelected,
  colorBrandBackgroundInvertedHover2: tokens.colorBrandBackgroundInvertedSelected,
  colorBrandBackgroundStatic1: tokens.colorCompoundBrandBackground,
  colorStrokeFocus2: tokens.colorStrokeFocus1,
  colorSubtleBackgroundSelected1: tokens.colorSubtleBackgroundSelected,
  colorNeutralBackground8: tokens.colorNeutralBackground5,
  colorBrandBackground1: tokens.colorBrandBackground,
  colorCompoundBrandForeground2Hover: tokens.colorCompoundBrandForeground1Hover,
  ...commonSpacings,
  ...commonFontSizes,
  ...commonLinetHeight,
};

const customTokensByMode: Modes = {
  light: {
    ...commonTokens,
    colorNeutralBackground7: tokens.colorBrandBackground,
    colorNeutralForeground5: tokens.colorBrandBackground,
    colorBrandBackgroundStatic1: tokens.colorBrandForegroundInverted,
    colorStrokeFocus2: tokens.colorBrandBackgroundInvertedSelected,
  },
  dark: {
    ...commonTokens,
    colorNeutralCardBackground2: tokens.colorNeutralBackground4,
    colorBrandBackgroundInvertedHover1: tokens.colorNeutralBackground1Pressed,
    colorBrandBackgroundInvertedHover2: tokens.colorNeutralBackground1Pressed,
    colorBrandBackground1: tokens.colorBrandBackgroundInverted,
  },
  contrast: {
    ...commonTokens,
    colorBrandBackgroundInvertedHover1: tokens.colorNeutralForeground1Hover,
    colorBrandBackgroundInvertedHover2: tokens.colorNeutralForeground1,
    colorSubtleBackgroundSelected1: tokens.colorNeutralForeground1,
    colorNeutralBackground8: tokens.colorNeutralForeground1,
    colorCompoundBrandForeground2Hover: tokens.colorNeutralForeground2Hover,
  },
};

export const brandTeamsColors = {
  10: `#2b2b40`,
  20: `#2f2f4a`,
  30: `#333357`,
  40: `#383966`,
  50: `#3d3e78`,
  60: `#444791`,
  70: `#4f52b2`,
  80: `#5b5fc7`,
  90: `#7579eb`,
  100: `#7f85f5`,
  110: `#9299f7`,
  120: `#aab1fa`,
  130: `#b6bcfa`,
  140: `#c5cbfa`,
  150: `#dce0fa`,
  160: `#e8ebfa`,
};

export const teamsLightTheme = createLightTheme(brandTeamsColors);
export const teamsDarkTheme = createTeamsDarkTheme(brandTeamsColors);

export const customTeamsLightTheme: CustomTheme = {
  ...teamsLightTheme,
  ...customTokensByMode.light,
};

export const customTeamsDarkTheme: CustomTheme = {
  ...teamsDarkTheme,
  ...customTokensByMode.dark,
};

export const customTeamsHighContrastTheme: CustomTheme = {
  ...teamsHighContrastTheme,
  ...customTokensByMode.contrast,
};

export const customTokens = themeToTokensObject(customTeamsLightTheme);

export const getTheme = (themeString: string): CustomTheme => {
  switch (themeString) {
    case 'dark':
      return customTeamsDarkTheme;
    case 'contrast':
      return customTeamsHighContrastTheme;
    default:
      return customTeamsLightTheme;
  }
};
